/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * TdLogin — second-stage credential form (email + 6-digit code). Caller
 * receives the form/inputs via the `getXxx()` accessors and wires the
 * submit handler that calls `authService.challengeLogin`.
 *
 * Lit light-DOM component. Reactive properties (`email`, `code`,
 * `emailDisabled`) drive the input bindings via Lit's `.value=` /
 * `?disabled=` syntax — no manual `whenDefined` ceremony needed since
 * Lit re-runs property bindings on each render, and the upgrade
 * pathway in webawesome-pro picks up pre-set values when the wa-input
 * class registers.
 */

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { ChallengeResponse } from '../services/auth.js';
import { t, LocaleController } from '../services/i18n.js';

/**
 * Shape passed to `setChallengeResult` after a successful challenge
 * round-trip. Extends `ChallengeResponse` (a `.passthrough()` Zod type)
 * with the `key` field the backend ships alongside `identifier` / `url`
 * — surfaced inline rather than tightening the schema in `auth.ts`,
 * which would creep beyond the conversion scope.
 */
export type ChallengeResultWithKey = ChallengeResponse & {
  ok: true;
  key?: string;
};

@customElement('td-login')
export class TdLogin extends LitElement {
  @property({ type: String }) email = '';
  @property({ type: String }) code = '';
  @property({ type: Boolean }) emailDisabled = false;

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override render(): TemplateResult {
    return html`
      <div class="bd-login-shell">
        <header class="bd-login-header wa-split">
          <a class="bd-logo" href="https://www.beyonddemise.com/">
            <img
              src="/assets/images/LogoWithTextTransparent.png"
              alt="Beyond Demise"
              title="Beyond Demise®"
            />
          </a>
          <a href="https://www.beyonddemise.com/">
            ${t('login.backToWebsite')}
          </a>
        </header>
        <div class="login-container">
          <wa-card class="card-header" appearance="accent">
            <div slot="header" class="wa-split">
              <h1>Beyond Demise</h1>
            </div>

            <div slot="footer" class="wa-split">
              <!-- role="alert" announces dynamically injected auth errors
                   to screen readers the moment [hidden] is removed (#243) -->
              <div
                id="error-message"
                class="error-message"
                role="alert"
                hidden
              ></div>
            </div>

            <p>${t('login.tagline')}</p>

            <form id="login-form" class="login-form">
              <wa-input
                type="email"
                name="email"
                id="email"
                label=${t('login.emailLabel')}
                .value=${this.email}
                ?disabled=${this.emailDisabled}
              ></wa-input>

              <!-- autocomplete="one-time-code" lets password managers /
                   iOS+Android keyboards offer the mailed code; numeric
                   inputmode brings up the digit keypad on touch (#243) -->
              <wa-input
                type="text"
                name="password"
                id="password"
                required
                placeholder=${t('login.codePlaceholder')}
                label=${t('login.codeLabel')}
                autocomplete="one-time-code"
                inputmode="numeric"
                .value=${this.code}
              ></wa-input>
              <div class="wa-gap-xl">
                <wa-button type="submit" size="m" variant="brand">
                  ${t('login.signIn')}
                </wa-button>
              </div>
            </form>
          </wa-card>
        </div>
      </div>
    `;
  }

  /** Returns the login form element, or `null` if not yet rendered. */
  getForm(): HTMLFormElement | null {
    return this.querySelector<HTMLFormElement>('#login-form');
  }

  /** Returns the error message host, or `null` if not yet rendered. */
  getErrorMessage(): HTMLElement | null {
    return this.querySelector<HTMLElement>('#error-message');
  }

  /**
   * Returns the email `<wa-input>` cast to `HTMLInputElement`. WebAwesome
   * inputs proxy `value` / `disabled` to a shadow `<input>`, so the cast
   * is safe at the property-access level we use here.
   */
  getEmailInput(): HTMLInputElement | null {
    return this.querySelector<HTMLInputElement>('wa-input[name="email"]');
  }

  /** Returns the password/code `<wa-input>` cast to `HTMLInputElement`. */
  getCodeInput(): HTMLInputElement | null {
    return this.querySelector<HTMLInputElement>('wa-input[name="password"]');
  }

  /** Returns the submit `<wa-button>` cast to `HTMLButtonElement`. */
  getSubmitButton(): HTMLButtonElement | null {
    return this.querySelector<HTMLButtonElement>('wa-button[type="submit"]');
  }

  /**
   * Populate the form from a successful challenge result: lock the
   * email field, pre-fill the code if the backend shipped it. Lit's
   * reactive properties re-render the inputs automatically.
   */
  setChallengeResult(
    challengeResult: ChallengeResultWithKey | undefined
  ): void {
    if (challengeResult?.ok) {
      this.email = challengeResult.identifier;
      this.emailDisabled = true;
      if (challengeResult.key !== undefined) {
        this.code = challengeResult.key;
      }
    }
  }
}
